"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cicToType, numberWithCommas } from "@/helpers/Helpers";
import Pagination from "../pagination/Pagination";
import { SortDirection } from "@/entities/ISort";
import type { CICType } from "@/entities/IProfile";
import { LeaderboardCollector } from "./LeaderboardCollector";
import { SearchModalDisplay, SearchWalletsDisplay } from "../searchModal/SearchModal";
import { commonApiFetch } from "@/services/api/common-api";
import { faSquareCaretUp, faSquareCaretDown } from "@fortawesome/free-solid-svg-icons";
const tableHeaderClassName = "tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-semibold tw-leading-4 tw-text-iron-400";
const tableCellClassName = "tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-px-4 tw-py-3 tw-text-center tw-text-sm tw-font-medium tw-leading-5 tw-text-iron-100";
interface Props {
  contract: string;
  nftId: number;
}
export const PAGE_SIZE = 25;
export async function fetchNftTdhResults(contract: string, nftId: number, walletFilter: string, page: number, sort: string, sort_direction: string) {
  const url = `tdh/nft`;
  const results = await commonApiFetch<{
    count: number;
    page: number;
    next: any;
    data: NftTDH[];
  }>({
    endpoint: `${url}/${contract}/${nftId}?${walletFilter}&page_size=${PAGE_SIZE}&page=${page}&sort=${sort}&sort_direction=${sort_direction}`
  });
  results.data.forEach((lead: NftTDH) => {
    lead.cic_type = cicToType(lead.cic_score);
  });
  return results;
}
export function setScrollPosition() {
  const top = document.getElementById("nft-leaderboard")?.offsetTop;
  if (top && window.scrollY > 0) {
    window.scrollTo({
      top: top,
      behavior: "smooth"
    });
  }
}
enum Sort {
  balance = "balance",
  tdh__raw = "tdh__raw",
  boosted_tdh = "boosted_tdh",
  total_balance = "total_balance",
  total_tdh__raw = "total_tdh__raw",
  total_boosted_tdh = "total_boosted_tdh",
}
interface NftTDH {
  id: number;
  contract: string;
  handle: string;
  pfp_url: string;
  consolidation_key: string;
  consolidation_display: string;
  balance: number;
  tdh: number;
  boost: number;
  boosted_tdh: number;
  tdh__raw: number;
  tdh_rank: number;
  total_balance: number;
  total_tdh: number;
  total_tdh__raw: number;
  total_boosted_tdh: number;
  rep_score: number;
  cic_score: number;
  primary_wallet: string;
  level: number;
  cic_type?: CICType | undefined;
}
export interface NftTDHRanked extends NftTDH {
  rank: number;
}
export default function NFTLeaderboard(props) {
  const $ = _c(70);
  const leaderboardSectionRef = useRef(null);
  const [page, setPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [leaderboard, setLeaderboard] = useState(t0);
  const [fetchingLeaderboard, setFetchingLeaderboard] = useState(true);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      sort: Sort.balance,
      sort_direction: SortDirection.DESC
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [sort, setSort] = useState(t1);
  const [showSearchModal, setShowSearchModal] = useState(false);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [searchWallets, setSearchWallets] = useState(t2);
  let t3;
  if ($[3] !== page || $[4] !== props.contract || $[5] !== props.nftId || $[6] !== searchWallets || $[7] !== sort.sort || $[8] !== sort.sort_direction) {
    t3 = async function fetchResults() {
      setFetchingLeaderboard(true);
      let walletFilter = "";
      if (searchWallets && searchWallets.length > 0) {
        walletFilter = `&search=${searchWallets.join(",")}`;
      }
      const response = await fetchNftTdhResults(props.contract, props.nftId, walletFilter, page, sort.sort, sort.sort_direction);
      setTotalResults(response.count);
      const data = response.data.map((lead, index) => {
        const rank = searchWallets.length > 0 ? lead.tdh_rank : index + 1 + (page - 1) * PAGE_SIZE;
        return {
          ...lead,
          rank
        };
      });
      setLeaderboard(data);
      setFetchingLeaderboard(false);
    };
    $[3] = page;
    $[4] = props.contract;
    $[5] = props.nftId;
    $[6] = searchWallets;
    $[7] = sort.sort;
    $[8] = sort.sort_direction;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const fetchResults = t3;
  let t4;
  if ($[10] !== fetchResults || $[11] !== page) {
    t4 = () => {
      if (page === 1) {
        fetchResults();
      } else {
        setPage(1);
      }
    };
    $[10] = fetchResults;
    $[11] = page;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== searchWallets || $[14] !== sort) {
    t5 = [sort, searchWallets];
    $[13] = searchWallets;
    $[14] = sort;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  useEffect(t4, t5);
  let t6;
  if ($[16] !== fetchResults) {
    t6 = () => {
      fetchResults();
    };
    $[16] = fetchResults;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== page) {
    t7 = [page];
    $[18] = page;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  useEffect(t6, t7);
  let t8;
  if ($[20] !== sort.sort || $[21] !== sort.sort_direction) {
    const getCaretClassName = function getCaretClassName(sortOption, sortDirection) {
      const active = sort.sort === sortOption && sort.sort_direction === sortDirection;
      return `tw-h-3 tw-w-3 tw-transition-colors ${active ? "tw-text-white" : "tw-text-iron-500 hover:tw-text-white"}`;
    };
    const printSortControls = function printSortControls(sortOption_0, label) {
      return <span className="tw-flex tw-flex-col tw-items-center"><button type="button" onClick={() => setSort({
          sort: sortOption_0,
          sort_direction: SortDirection.ASC
        })} className="tw-m-0 tw-flex tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0" aria-label={`Sort ${label} ascending`}><FontAwesomeIcon icon={faSquareCaretUp} className={getCaretClassName(sortOption_0, SortDirection.ASC)} /></button><button type="button" onClick={() => setSort({
          sort: sortOption_0,
          sort_direction: SortDirection.DESC
        })} className="tw-m-0 tw-flex tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0" aria-label={`Sort ${label} descending`}><FontAwesomeIcon icon={faSquareCaretDown} className={getCaretClassName(sortOption_0, SortDirection.DESC)} /></button></span>;
    };
    t8 = function printSortableHeader(label_0, sortOption_1, t9) {
      const className = t9 === undefined ? "" : t9;
      return <th scope="col" className={`${tableHeaderClassName} ${className}`}><span className="tw-flex tw-items-center tw-justify-center tw-gap-1.5">{label_0}{printSortControls(sortOption_1, label_0)}</span></th>;
    };
    $[20] = sort.sort;
    $[21] = sort.sort_direction;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const printSortableHeader = t8;
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = newPage => {
      setPage(newPage);
      leaderboardSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    };
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleLeaderboardPageChange = t9;
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <h3 className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-100">Collectors leaderboard</h3>;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== searchWallets) {
    t11 = <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">{t10}<SearchWalletsDisplay searchWallets={searchWallets} setSearchWallets={setSearchWallets} setShowSearchModal={setShowSearchModal} /></div>;
    $[25] = searchWallets;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <tr><th colSpan={2} aria-hidden="true" className="tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-px-4 tw-py-3" /><th colSpan={3} scope="colgroup" className="tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-l tw-border-solid tw-border-iron-800 tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-semibold tw-leading-4 tw-text-iron-300">This Card</th><th colSpan={3} scope="colgroup" className="tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-l tw-border-solid tw-border-iron-800 tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-semibold tw-leading-4 tw-text-iron-300">Total</th></tr>;
    t13 = <th scope="col" className="tw-w-20 tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-semibold tw-leading-4 tw-text-iron-400">Rank</th>;
    $[27] = t12;
    $[28] = t13;
  } else {
    t12 = $[27];
    t13 = $[28];
  }
  let t14;
  if ($[29] !== totalResults) {
    t14 = totalResults > 0 && `x${totalResults.toLocaleString()}`;
    $[29] = totalResults;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14) {
    t15 = <th scope="col" className="tw-min-w-[18rem] tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-leading-4 tw-text-iron-400">Collector{" "}{t14}</th>;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[33] !== printSortableHeader) {
    t16 = printSortableHeader("Balance", Sort.balance, "tw-border-l");
    t17 = printSortableHeader("TDH", Sort.boosted_tdh);
    t18 = printSortableHeader("Unweighted TDH", Sort.tdh__raw);
    t19 = printSortableHeader("Balance", Sort.total_balance, "tw-border-l");
    t20 = printSortableHeader("TDH", Sort.total_boosted_tdh);
    t21 = printSortableHeader("Unweighted TDH", Sort.total_tdh__raw);
    $[33] = printSortableHeader;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
    $[39] = t21;
  } else {
    t16 = $[34];
    t17 = $[35];
    t18 = $[36];
    t19 = $[37];
    t20 = $[38];
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t15 || $[41] !== t16 || $[42] !== t17 || $[43] !== t18 || $[44] !== t19 || $[45] !== t20 || $[46] !== t21) {
    t22 = <thead>{t12}<tr>{t13}{t15}{t16}{t17}{t18}{t19}{t20}{t21}</tr></thead>;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== leaderboard) {
    t23 = leaderboard.map(_temp);
    $[48] = leaderboard;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t23) {
    t24 = <tbody>{t23}</tbody>;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t22 || $[53] !== t24) {
    t25 = <div className="tw-overflow-x-auto"><table className="tw-w-full tw-min-w-[1040px] tw-border-collapse">{t22}{t24}</table></div>;
    $[52] = t22;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== fetchingLeaderboard || $[56] !== leaderboard.length) {
    t26 = leaderboard.length === 0 && !fetchingLeaderboard && <div className="tw-py-5 tw-text-sm tw-font-medium tw-text-iron-400">No Results found</div>;
    $[55] = fetchingLeaderboard;
    $[56] = leaderboard.length;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== page || $[59] !== totalResults) {
    t27 = totalResults > 0 && <div className="tw-flex tw-justify-center tw-pb-3 tw-pt-4"><Pagination page={page} pageSize={PAGE_SIZE} totalResults={totalResults} setPage={handleLeaderboardPageChange} /></div>;
    $[58] = page;
    $[59] = totalResults;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== searchWallets || $[62] !== showSearchModal) {
    t28 = <SearchModalDisplay show={showSearchModal} setShow={setShowSearchModal} searchWallets={searchWallets} setSearchWallets={setSearchWallets} />;
    $[61] = searchWallets;
    $[62] = showSearchModal;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t11 || $[65] !== t25 || $[66] !== t26 || $[67] !== t27 || $[68] !== t28) {
    t29 = <section ref={leaderboardSectionRef} className="tw-scroll-mt-24 tw-pt-8" id="nft-leaderboard">{t11}{t25}{t26}{t27}{t28}</section>;
    $[64] = t11;
    $[65] = t25;
    $[66] = t26;
    $[67] = t27;
    $[68] = t28;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  return t29;
}
function _temp(lead_0) {
  return <tr key={lead_0.consolidation_key} className="odd:tw-bg-transparent even:tw-bg-iron-900/45 hover:tw-bg-iron-900/70"><td className="tw-whitespace-nowrap tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-px-4 tw-py-3 tw-text-center tw-text-sm tw-font-semibold tw-leading-5 tw-text-iron-100">{numberWithCommas(lead_0.rank)}</td><td className="tw-border-0 tw-border-b tw-border-solid tw-border-iron-800 tw-px-4 tw-py-3"><LeaderboardCollector handle={lead_0.handle} consolidationKey={lead_0.consolidation_key} consolidationDisplay={lead_0.consolidation_display} pfp={lead_0.pfp_url} level={lead_0.level} /></td><td className={`${tableCellClassName} tw-border-l`}>{numberWithCommas(lead_0.balance)}</td><td className={tableCellClassName}>{numberWithCommas(Math.round(lead_0.boosted_tdh))}</td><td className={tableCellClassName}>{numberWithCommas(Math.round(lead_0.tdh__raw))}</td><td className={`${tableCellClassName} tw-border-l`}>{numberWithCommas(lead_0.total_balance)}</td><td className={tableCellClassName}>{numberWithCommas(Math.round(lead_0.total_boosted_tdh))}</td><td className={tableCellClassName}>{numberWithCommas(Math.round(lead_0.total_tdh__raw))}</td></tr>;
}